import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Divider, Stack} from "@mui/material";
import {useMutation} from "react-query";
import Typography from "@mui/material/Typography";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {IncertConfig, IncertCredentials} from "crqlar-react-common/dist/models/integrations/incert/incert.config";
import {LoadingButton} from "@mui/lab";
import {MbscCalendarEvent} from "@mobiscroll/react";
import {IncertService} from "crqlar-react-common/dist/services/integrations/incert/incert.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
    incertConfigData: UseEntityData<IncertConfig>
}

const IncertConfigForm = ({incertConfigData}: Props) => {

    const {t} = useTranslation()
    const navigate = useNavigate()
    const readOnlyUser = useReadOnly()

    const incertConfig = incertConfigData.item
    const nameMissing = isRequiredTextMissing(incertConfigData, incertConfig.name)
    const setIncertConfig = incertConfigData.setItem

    const checkConnection = useMutation(
        ['checkIncertConfig', incertConfig.credentials],
        async (event: MbscCalendarEvent) => {
            let success = false
            try {
                if (incertConfig.credentials) {
                    await IncertService.checkConnection(incertConfig);
                    success = true
                }
            } catch (error) {
            }

            enqueueSnackbar(
                success ? t('Connection successful') : t('Connection failed'),
                {
                    variant: success ? 'success' : 'error',
                    anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
                }
            )
        }
    )

    const updateField = (field: keyof IncertConfig, value: any) => {
        setIncertConfig({...incertConfig, [field]: value})
    }

    const updateCredentialsField = (field: keyof IncertCredentials, value: any) => {
        setIncertConfig({
            ...incertConfig,
            credentials: {
                ...incertConfig.credentials,
                [field]: value
            }
        })
    }

    const headerSection = (
        <SettingsEntityHeader
            title={incertConfig.name}
            data={incertConfigData}
            back={() => navigate('/system')}
        />
    )

    const credentialsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Integration name')}
                    tooltip={t('Internal label for identifying this Incert integration configuration.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={incertConfig.name || ''}
                        error={nameMissing}
                        fullWidth
                        onChange={e => updateField('name', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('API Key')}
                    tooltip={t('Credential used to authorize requests to the Incert API.')}
                >
                    <TextField
                        id={'api-key'}
                        size={'small'}
                        value={incertConfig.credentials?.apiKey || ''}
                        fullWidth
                        type={'password'}
                        onChange={e => updateCredentialsField('apiKey', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('API URL')}
                    tooltip={t('Base Incert API endpoint used by this integration.')}
                >
                    <TextField
                        id={'api-url'}
                        size={'small'}
                        value={incertConfig.credentials?.apiUrl || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('apiUrl', e.target.value)}
                    />
                </Property>

                <Stack direction={'row'} justifyContent={'flex-start'}>
                    <LoadingButton
                        disabled={readOnlyUser}
                        loading={checkConnection.isLoading}
                        onClick={checkConnection.mutate}
                        variant={'outlined'}
                    >
                        {t('Test connection')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={incertConfigData.saving}
                onDelete={() => incertConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {incertConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    return (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {credentialsSection}

                {
                    !!incertConfig.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </>
    )

}

export default IncertConfigForm;
